import { isRecord, requestJson } from "./http.js";

/** A new device waiting to be enrolled; it shows the code the owner types on a trusted device. */
export interface PendingDeviceApproval {
  readonly id: string;
  readonly deviceName: string;
  readonly ip: string;
  readonly createdAt: number;
  readonly expiresAt: number;
}

export interface IssuedDeviceApproval {
  readonly id: string;
  readonly code: string;
  readonly expiresAt: number;
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

function isPendingApproval(value: unknown): value is PendingDeviceApproval {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["deviceName"] === "string" &&
    typeof value["ip"] === "string" &&
    typeof value["createdAt"] === "number" &&
    typeof value["expiresAt"] === "number";
}

/** This device asks to be enrolled. */
export async function requestDeviceApproval(): Promise<IssuedDeviceApproval> {
  const body = await requestJson("/api/v1/devices/approvals", { method: "POST", body: {} });
  if (!isRecord(body) || typeof body["id"] !== "string" || typeof body["code"] !== "string" || typeof body["expiresAt"] !== "number") {
    throw unexpected();
  }
  return { id: body["id"], code: body["code"], expiresAt: body["expiresAt"] };
}

/** Withdraws this device's own request. */
export async function withdrawDeviceApproval(id: string): Promise<void> {
  await requestJson(`/api/v1/devices/approvals/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Whether the current sign-in's device is enrolled. */
export async function fetchDeviceTrusted(): Promise<boolean> {
  const body = await requestJson("/api/v1/auth/me");
  return isRecord(body) && body["deviceTrusted"] === true;
}

/** Requests from new devices of this account; only a trusted device may list them. */
export async function fetchDeviceApprovals(): Promise<PendingDeviceApproval[]> {
  const body = await requestJson("/api/v1/devices/approvals");
  if (!isRecord(body) || !Array.isArray(body["approvals"]) || !body["approvals"].every(isPendingApproval)) {
    throw unexpected();
  }
  return body["approvals"].filter(isPendingApproval);
}

/** A trusted device confirms a new one with the code it shows. */
export async function approveDevice(id: string, code: string): Promise<void> {
  await requestJson(`/api/v1/devices/approvals/${encodeURIComponent(id)}`, { method: "POST", body: { code } });
}
